import { useEffect, useState } from 'react';
import { getCoveringShiftClose } from '../api/shifts';
import type { CoveringShiftClose } from '../types/shiftCashBreakdown';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';
import { Icon } from './ui/Icon';

// Misma convención que el resto del módulo: el formateo vive junto a quien lo usa, siempre en la
// zona de la TIENDA (nunca la del dispositivo — un corte se lee en la hora del mostrador).
function formatDateTime(value: string, timeZone: string = DEFAULT_STORE_TIMEZONE) {
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value));
}

/**
 * Aviso previo a tocar un movimiento que un corte YA CERRADO contó.
 *
 * Un corte cerrado es la foto de lo que se contó esa noche y NO se rehace — esa es la regla, no un
 * defecto. Lo que faltaba era decirlo a tiempo: antes uno se enteraba solo si después abría justo
 * ese corte. Aparece únicamente cuando hay un corte cubriendo el movimiento; en el caso normal el
 * componente no dibuja nada y nadie ve un aviso de más.
 *
 * Dos redacciones porque son dos situaciones distintas:
 * - EJECUTAR (dueño/gerente anula o modifica): la consecuencia es inmediata y suya.
 * - PEDIR (`asRequest`, empleado que solicita el cambio): no puede romper nada — no ejecuta —, pero
 *   necesita saber que aunque se lo aprueben su corte no se va a recalcular. El aviso existe para
 *   evitar una expectativa falsa, no un error.
 *
 * Solo lectura y tolerante a fallos: si la consulta falla (permiso, red), no bloquea la acción ni
 * muestra un error — el aviso es una cortesía, no una validación.
 */
export function ClosedShiftNotice({ storeId, at, timeZone, action, asRequest = false }: {
  storeId: string | null | undefined;
  /** Fecha EFECTIVA del movimiento (la que declara cuándo ocurrió), no la de captura. */
  at: string | null | undefined;
  timeZone?: string;
  /** Cómo se llama lo que está por hacerse, para redactar la consecuencia. */
  action: 'anular' | 'modificar';
  /** true cuando se PIDE el cambio en vez de aplicarlo (VoidRequestModal). */
  asRequest?: boolean;
}) {
  const [close, setClose] = useState<CoveringShiftClose | null>(null);

  useEffect(() => {
    if (!storeId || !at) { setClose(null); return; }
    let cancelled = false;
    getCoveringShiftClose({ storeId, at })
      .then((response) => { if (!cancelled) setClose(response.close); })
      .catch(() => { if (!cancelled) setClose(null); });
    return () => { cancelled = true; };
  }, [storeId, at]);

  if (!close) return null;
  const folio = close.folio ?? 'ya cerrado';
  return (
    <div className="closed-shift-notice">
      <span className="closed-shift-notice__ic"><Icon name="shift" size={16} /></span>
      <div>
        <strong>Este movimiento ya lo contó el corte {folio}</strong>
        <span>
          {/* El formato es-MX termina en "a.m."/"p.m." (con punto): no se le añade otro. */}
          {close.employeeName ? `${close.employeeName} cerró ese turno` : 'Ese turno se cerró'} el {formatDateTime(close.closedAt, timeZone)}{' '}
          {asRequest
            ? <>Aunque te aprueben la solicitud, ese corte no se rehace: el efectivo esperado que quedó guardado sigue siendo el de esa noche. Si se aplica, el Detalle del turno del corte {folio} lo señalará.</>
            : <>Al {action} no se rehace el corte: el efectivo esperado que quedó guardado sigue siendo el de esa noche. El Detalle del turno del corte {folio} avisará que un movimiento cambió después.</>}
        </span>
      </div>
    </div>
  );
}
